import styled from 'styled-components';
import { Button } from '../../../components/uielements';
import { key } from 'styled-theme';

const desktopBreakpoint = key('breakpoints.desktop', '1024px');
const tabletBreakpoint = key('breakpoints.tablet', '820px');
const mobileBreakpoint = key('breakpoints.mobile', '480px');

const gap = {
  tiny: key('sizes.gap.tiny', '4px'),
  small: key('sizes.gap.small', '8px'),
  normal: key('sizes.gap.normal', '16px'),
  medium: key('sizes.gap.medium', '24px'),
  big: key('sizes.gap.big', '32px'),
};

const radius = {
  tiny: key('sizes.borderRadius.tiny', '4px'),
  small: key('sizes.borderRadius.small', '8px'),
};

const font = {
  small: key('sizes.font.small', '12px'),
  normal: key('sizes.font.normal', '14px'),
  medium: key('sizes.font.medium', '16px'),
};

export const Container = styled.div`
  max-width: 90%;
  margin: 0 auto;
  padding: ${gap.big};
  padding-top: 100px;

  @media (max-width: ${desktopBreakpoint}) {
    max-width: 95%;
  }

  @media (max-width: ${tabletBreakpoint}) {
    max-width: 100%;
    padding: ${gap.medium};
    padding-top: 80px;
  }

  @media (max-width: ${mobileBreakpoint}) {
    padding: ${gap.normal};
    padding-top: 64px;
  }
`;

export const Title = styled.h1`
  font-size: 1.8rem;
  margin-bottom: ${gap.big};
  color: #2c3e50;
  text-align: center;
  padding-bottom: ${gap.normal};

  @media (max-width: ${tabletBreakpoint}) {
    font-size: 1.5rem;
    margin-bottom: ${gap.medium};
  }

  @media (max-width: ${mobileBreakpoint}) {
    font-size: 1.25rem;
    padding-bottom: ${gap.small};
  }
`;

export const Main = styled.main`
  display: flex;
  flex-direction: column;
  gap: ${gap.big};

  @media (max-width: ${tabletBreakpoint}) {
    gap: ${gap.medium};
  }

  @media (max-width: ${mobileBreakpoint}) {
    gap: ${gap.normal};
  }
`;

export const ReportContent = styled.div`
  background-color: white;
  padding: ${gap.big};
  border-radius: ${radius.tiny};

  h3 {
    color: #3498db;
    margin-top: ${gap.medium};
    margin-bottom: ${gap.normal};
  }

  p {
    margin-bottom: ${gap.normal};
    line-height: 1.6;
  }

  a {
    color: #3498db;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  @media (max-width: ${tabletBreakpoint}) {
    padding: ${gap.medium};

    h3 {
      margin-top: ${gap.normal};
      margin-bottom: ${gap.small};
    }
  }

  @media (max-width: ${mobileBreakpoint}) {
    padding: ${gap.normal};

    p {
      font-size: ${font.normal};
      margin-bottom: ${gap.small};
    }
  }
`;

export const StatusBadge = styled.div`
  display: inline-block;
  padding: ${gap.small} ${gap.normal};
  border-radius: 20px;
  font-weight: 600;
  font-size: ${font.medium};
  margin-bottom: ${gap.normal};
  background-color: ${(props) =>
    props.status === 'success'
      ? '#e6f7ee'
      : props.status === 'pending'
        ? '#fff7e6'
        : props.status === 'error'
          ? '#fff1f0'
          : '#e6f7ff'};
  color: ${(props) =>
    props.status === 'success'
      ? '#52c41a'
      : props.status === 'pending'
        ? '#faad14'
        : props.status === 'error'
          ? '#f5222d'
          : '#1890ff'};
  border: 1px solid
    ${(props) =>
      props.status === 'success'
        ? '#b7eb8f'
        : props.status === 'pending'
          ? '#ffe58f'
          : props.status === 'error'
            ? '#ffa39e'
            : '#91d5ff'};

  @media (max-width: ${mobileBreakpoint}) {
    font-size: ${font.normal};
    padding: ${gap.tiny} ${gap.small};
  }
`;

export const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  gap: ${gap.small};
  margin: ${gap.big} 0 ${gap.medium} 0;

  @media (max-width: ${tabletBreakpoint}) {
    gap: ${gap.small};
    margin: ${gap.medium} 0;
  }

  @media (max-width: ${mobileBreakpoint}) {
    flex-direction: column;
    margin: ${gap.normal} 0;
  }
`;

export const ActionRow = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 1 1 200px;
  min-width: 0;
  background-color: #ffffff;
  border: none;
  border-radius: ${radius.small};
  padding: ${gap.small} ${gap.normal};
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  transition:
    transform 0.15s ease,
    box-shadow 0.15s ease;
  text-align: left;
  font-family: inherit;

  &:hover {
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: scale(0.99);
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
    box-shadow: none;
  }

  @media (max-width: ${mobileBreakpoint}) {
    flex: 1 1 auto;
    width: 100%;
    padding: ${gap.small} ${gap.normal};
    border-radius: ${radius.tiny};
  }
`;

export const ActionRowLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${gap.small};
  min-width: 0;

  @media (max-width: ${mobileBreakpoint}) {
    gap: ${gap.small};
  }
`;

export const ActionIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: ${radius.tiny};
  background-color: #ece9fb;
  color: #5b3df6;
  flex-shrink: 0;

  @media (max-width: ${mobileBreakpoint}) {
    width: 30px;
    height: 30px;
  }
`;

export const ActionText = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

export const ActionTitle = styled.span`
  font-weight: 700;
  font-size: ${font.normal};
  color: #1f1f1f;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: ${mobileBreakpoint}) {
    font-size: ${font.normal};
    letter-spacing: 0.2px;
  }
`;

export const ActionChevron = styled.div`
  display: flex;
  align-items: center;
  color: #5b3df6;
  flex-shrink: 0;
  margin-left: ${gap.small};
`;

export const LinkButton = styled(Button)`
  flex: 1 1 200px;
  border: none;
  border-radius: 999px;
  padding: ${gap.small} ${gap.medium};
  background: linear-gradient(90deg, #5b3df6, #3173da);
  color: #ffffff;
  font-weight: 700;
  font-size: ${font.normal};
  letter-spacing: 0.5px;
  text-transform: uppercase;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${gap.small};
  white-space: nowrap;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.92;
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }

  @media (max-width: ${mobileBreakpoint}) {
    flex: 1 1 auto;
    width: 100%;
    padding: ${gap.small} ${gap.normal};
    font-size: ${font.normal};
    letter-spacing: 0.3px;
  }
`;
